// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import styles from '@app/features/channel/components/GifPicker.module.css';
import type {GifPickerState} from '@app/features/channel/components/pickers/gif/GifPickerState';
import {PickerSearchInput} from '@app/features/channel/components/shared/PickerSearchInput';
import pickerSearchInputStyles from '@app/features/channel/components/shared/PickerSearchInput.module.css';
import {isIMEComposing} from '@app/features/messaging/utils/IMECompositionUtils';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import PoweredByKlipySvg from '@app/media/images/powered-by-klipy.svg?react';
import {useLingui} from '@lingui/react/macro';
import {ArrowLeftIcon, DownloadSimpleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
export const GifPickerHeader = observer(
	({
		store,
		inputRef,
		onOpenImport,
	}: {
		store: GifPickerState;
		inputRef: React.RefObject<HTMLInputElement | null> | React.RefObject<HTMLInputElement>;
		onOpenImport?: () => void;
	}) => {
		const {t} = useLingui();
		const gifProviderName = RuntimeConfig.gifProviderDisplayName;
		const showKlipyWatermark = RuntimeConfig.gifAttributionRequired && RuntimeConfig.gifProvider === 'klipy';
		if (store.view !== 'default') {
			const title = (() => {
				if (store.view === 'trending') return t`Trending GIFs`;
				if (store.view === 'favorites') return t`Favorite GIFs`;
				return t`GIFs`;
			})();
			return (
				<div className={styles.searchBarContainer}>
					<div className={styles.searchBarTitleWrapper}>
						<div className={styles.searchBarTitleLeft}>
							<FocusRing offset={-2}>
								<button
									type="button"
									className={styles.searchBarBackButton}
									onClick={store.goToDefaultView}
									aria-label={t`Back to GIF categories`}
								>
									<ArrowLeftIcon weight="regular" />
								</button>
							</FocusRing>
							<div className={styles.searchBarTitle}>{title}</div>
						</div>
						{store.view === 'favorites' && onOpenImport != null && (
							<FocusRing offset={-2}>
								<button
									type="button"
									className={styles.searchBarBackButton}
									onClick={onOpenImport}
									aria-label={t`Import GIFs`}
								>
									<DownloadSimpleIcon weight="regular" />
								</button>
							</FocusRing>
						)}
					</div>
				</div>
			);
		}
		const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
			if (isIMEComposing(event)) {
				return;
			}
			if (event.key === 'Enter') {
				event.preventDefault();
				store.flushSearch();
			}
		};
		return (
			<PickerSearchInput
				value={store.searchTerm}
				onChange={store.setSearchTerm}
				placeholder={t`Search ${gifProviderName}`}
				inputRef={inputRef}
				showBackButton={!!store.searchTerm.trim()}
				onBackButtonClick={() => store.setSearchTerm('')}
				onKeyDown={handleKeyDown}
				rightCustomElement={
					showKlipyWatermark ? <PoweredByKlipySvg className={pickerSearchInputStyles.poweredByKlipy} /> : null
				}
			/>
		);
	},
);
